// Public "Apply for a job" page — the URL you can hand to a candidate:
//   https://hr.zucol.in/apply                    (generic — candidate picks role)
//   https://hr.zucol.in/apply?req=<requisitionId> (pre-selects one open role)
//
// No login. Renders standalone (no sidebar / no auth chrome). Backed by
// server/routes/apply.js — see that file for the guards (rate limit,
// resume size cap, honeypot, duplicate detection).

import React, { useState, useEffect } from "react";
import { Upload, Send, CheckCircle } from "lucide-react";
import { BASE } from "../api";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";
const labelCls = "block text-[14px] font-semibold text-slate-600 uppercase tracking-wide mb-1";

export default function PublicApply() {
  // Support both ?req=<id> in the path-based URL and #apply?req=<id> in
  // the hash-based URL, so HR can share whichever the org prefers.
  const rawQuery = (window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : window.location.search);
  const initialReq = new URLSearchParams(rawQuery).get('req') || '';
  const [cfg, setCfg] = useState({ company_name: '' });
  const [openings, setOpenings] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    request_id: initialReq,
    position: '',
    current_company: '', experience_years: '',
    current_ctc: '', expected_ctc: '', notice_period: '',
    resume: null, resumeName: '',
    // Honeypot — MUST stay empty. Rendered off-screen so bots fill it.
    company_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${BASE}/api/apply/config`).then(r => r.json()).then(setCfg).catch(() => {});
    fetch(`${BASE}/api/apply/openings`).then(r => r.json()).then(d => setOpenings(d.openings || [])).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');

    // Client-side belt-and-braces before we POST — server checks again.
    if (!form.name.trim()) return setErr('Please enter your name');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setErr('Please enter a valid email');
    if (!form.phone.trim()) return setErr('Please enter your phone number');
    const chosenReq = form.request_id ? openings.find(o => String(o.id) === String(form.request_id)) : null;
    const positionForServer = chosenReq ? chosenReq.position : form.position.trim();
    if (!positionForServer) return setErr('Please pick the role you are applying for');

    setSubmitting(true);
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('email', form.email);
    fd.append('phone', form.phone);
    if (form.request_id) fd.append('request_id', form.request_id);
    fd.append('position', positionForServer);
    if (form.current_company)   fd.append('current_company', form.current_company);
    if (form.experience_years)  fd.append('experience_years', form.experience_years);
    if (form.current_ctc)       fd.append('current_ctc', form.current_ctc);
    if (form.expected_ctc)      fd.append('expected_ctc', form.expected_ctc);
    if (form.notice_period)     fd.append('notice_period', form.notice_period);
    if (form.company_url)       fd.append('company_url', form.company_url); // honeypot
    if (form.resume)            fd.append('resume', form.resume);

    try {
      const res = await fetch(`${BASE}/api/apply`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setDone(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} className="text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Application received</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            {done.message || `Thanks! We've received your application. Our team will be in touch.`}
          </p>
          {done.existing && (
            <p className="text-[14px] text-slate-500 mt-3 italic">
              (An application with these contact details was already on file — we haven't created a duplicate.)
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          {cfg.logo_url && <img src={`${BASE}${cfg.logo_url}`} alt="" className="h-14 mx-auto mb-3" />}
          <h1 className="text-2xl font-extrabold text-slate-900">{cfg.company_name || 'Careers'}</h1>
          <p className="text-sm text-slate-600 mt-1">Apply for a role below and our team will get back to you.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          {/* Honeypot — visually hidden, autocomplete off. Bots fill this. */}
          <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="company_url">Company URL (leave blank)</label>
            <input type="text" id="company_url" name="company_url" tabIndex="-1" autoComplete="off"
                   value={form.company_url} onChange={e => upd('company_url', e.target.value)} />
          </div>

          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">About you</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="sm:col-span-2">
              <label className={labelCls}>Full Name <span className="text-red-500">*</span></label>
              <input className={inputCls} value={form.name} onChange={e => upd('name', e.target.value)} required maxLength={100} />
            </div>
            <div>
              <label className={labelCls}>Email <span className="text-red-500">*</span></label>
              <input type="email" className={inputCls} value={form.email} onChange={e => upd('email', e.target.value)} required />
            </div>
            <div>
              <label className={labelCls}>Phone <span className="text-red-500">*</span></label>
              <input type="tel" className={inputCls} value={form.phone} onChange={e => upd('phone', e.target.value.replace(/[^\d+\-() ]/g, ''))} required />
            </div>
          </div>

          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Role</h2>
          <div className="grid grid-cols-1 gap-4 mb-6">
            {openings.length > 0 ? (
              <div>
                <label className={labelCls}>Applying for <span className="text-red-500">*</span></label>
                <select className={inputCls} value={form.request_id} onChange={e => upd('request_id', e.target.value)} required>
                  <option value="">Select a role…</option>
                  {openings.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.position}{o.department ? ` — ${o.department}` : ''}{o.location ? ` · ${o.location}` : ''}{o.company ? ` · ${o.company}` : ''}
                    </option>
                  ))}
                  <option value="">Other / not listed</option>
                </select>
              </div>
            ) : null}
            {(!form.request_id || openings.length === 0) && (
              <div>
                <label className={labelCls}>Role you are applying for {(!openings.length) && <span className="text-red-500">*</span>}</label>
                <input className={inputCls} value={form.position} onChange={e => upd('position', e.target.value)} placeholder="e.g. Sr Software Engineer" />
              </div>
            )}
          </div>

          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Current role <span className="text-slate-400 font-normal normal-case text-xs">— optional but helpful</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className={labelCls}>Current company</label>
              <input className={inputCls} value={form.current_company} onChange={e => upd('current_company', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Total experience (years)</label>
              <input type="number" step="0.1" min="0" className={inputCls} value={form.experience_years} onChange={e => upd('experience_years', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Current CTC (₹, annual)</label>
              <input type="number" min="0" className={inputCls} value={form.current_ctc} onChange={e => upd('current_ctc', e.target.value)} placeholder="e.g. 800000" />
            </div>
            <div>
              <label className={labelCls}>Expected CTC (₹, annual)</label>
              <input type="number" min="0" className={inputCls} value={form.expected_ctc} onChange={e => upd('expected_ctc', e.target.value)} placeholder="e.g. 1200000" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Notice period</label>
              <select className={inputCls} value={form.notice_period} onChange={e => upd('notice_period', e.target.value)}>
                <option value="">Select…</option>
                {['Immediate', '15 days', '30 days', '45 days', '60 days', '90 days'].map(n => <option key={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Resume <span className="text-slate-400 font-normal normal-case text-xs">— PDF, DOC or DOCX, up to 5 MB</span></h2>
          <label className="flex items-center justify-center gap-2 p-4 mb-6 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 cursor-pointer text-slate-600 text-sm hover:border-emerald-400 hover:bg-emerald-50">
            <Upload size={16} className="text-emerald-600" />
            {form.resumeName || 'Click to upload your resume'}
            <input type="file" accept=".pdf,.doc,.docx" className="hidden"
              onChange={e => { const f = e.target.files[0]; if (f) { upd('resume', f); upd('resumeName', f.name); } }} />
          </label>

          {err && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>}

          <button type="submit" disabled={submitting}
            className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={16} />{submitting ? 'Submitting…' : 'Submit Application'}
          </button>
          <p className="text-[14px] text-slate-500 text-center mt-3">
            We'll only use your contact details to reach out about roles at {cfg.company_name || 'our company'}.
          </p>
        </form>
      </div>
    </div>
  );
}
